import React, { createContext, useContext, useState } from "react";
import Axios from "axios";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const [cartItems, setCartItems] = useState([]); // ✅ بيانات المنتجات داخل السلة

  // ✅ إضافة منتج للسلة
  const addToCart = async (product_id, quantity = 1) => {
    setLoading(true);
    setMessage("");
    setError("");

    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_URL}/cart/addToCart.php`,
        { product_id: Number(product_id), quantity: Number(quantity) },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (res.data?.status === "success") {
        const msg = res.data?.message || "تمت الإضافة للسلة بنجاح ✅";
        setMessage(msg);
        await getCartCount();
        return { success: true, data: res.data, message: msg };
      } else {
        const msg = res.data?.message || "تعذر إضافة المنتج للسلة";
        setError(msg);
        return { success: false, data: res.data, message: msg };
      }
    } catch (err) {
      const msg = err.response?.data?.message || "فشل الاتصال بالسيرفر";
      setError(msg);
      return { success: false, error: err, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // ✅ حذف أو تقليل كمية منتج من السلة
  const removeFromCart = async (product_id, quantity = 1) => {
    setLoading(true);
    setMessage("");
    setError("");

    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_URL}/cart/removeFromCart.php`,
        { product_id: Number(product_id), quantity: Number(quantity) },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (res.data?.status === "success") {
        const msg = res.data?.message || "تمت إزالة المنتج من السلة ✅";
        setMessage(msg);
        await getCartCount(); // تحديث عداد السلة
        return { success: true, data: res.data, message: msg };
      } else {
        const msg = res.data?.message || "فشل في إزالة المنتج";
        setError(msg);
        return { success: false, data: res.data, message: msg };
      }
    } catch (err) {
      const msg = err.response?.data?.message || "خطأ في الاتصال بالسيرفر";
      setError(msg);
      return { success: false, error: err, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // ✅ عدد العناصر في السلة
  const getCartCount = async () => {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/cart/getCartCount.php`,
        { withCredentials: true }
      );

      if (res.data?.status === "success") {
        setCartCount(Number(res.data.count));
        return res.data.count;
      } else {
        setCartCount(0);
        return 0;
      }
    } catch (err) {
      console.error("فشل جلب عدد السلة:", err);
      setCartCount(0);
      return 0;
    }
  };

  // ✅ جلب بيانات المنتجات داخل السلة
  const getCartItems = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/cart/getFromCart.php`,
        { withCredentials: true }
      );

      if (res.data?.status === "success") {
        setCartItems(res.data.cart);
        return { success: true, data: res.data.cart };
      } else {
        const msg = res.data?.message || "فشل في جلب السلة";
        setError(msg);
        return { success: false, message: msg };
      }
    } catch (err) {
      const msg = err.response?.data?.message || "خطأ في الاتصال";
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  // ✅ تفريغ السلة بالكامل
  const clearCart = async () => {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_URL}/cart/clearCart.php`,
        {},
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );

      if (res.data?.status === "success") {
        setCartItems([]);
        setCartCount(0);
        const msg = res.data?.message || "تم تفريغ السلة بنجاح ✅";
        setMessage(msg);
        return { success: true, message: msg };
      } else {
        const msg = res.data?.message || "فشل في تفريغ السلة";
        setError(msg);
        return { success: false, message: msg };
      }
    } catch (err) {
      const msg = err.response?.data?.message || "خطأ في الاتصال";
      setError(msg);
      return { success: false, message: msg };
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{
        addToCart,
        getCartCount,
        getCartItems,
        setCartItems,
        removeFromCart,
        clearCart,
        cartItems,
        cartCount,
        loading,
        message,
        error,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);
